import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";

import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import InfoIcon from "@mui/icons-material/Info";

import * as actions from "../../store/actions/actionIndex";

const CallStatisticsModal = (props) => {
  const { open, onClose, agentId, onCallAgentHistory, loading, date, recentHisCalls = [] } = props;
  const [callType, setSelectedCallType] = useState(props.callType || "");
  const [callDisp, setCallDisp] = useState(props.callDisp || "")
  
  useEffect(() => {
    if (agentId && open) {
      onCallAgentHistory(agentId, date, 0, callDisp, callType);
    }
  }, [agentId, open, callType, date, onCallAgentHistory, callDisp]);

  const callsArray = Array.isArray(recentHisCalls) ? recentHisCalls : [];

  const { inboundCount, outboundCount, totalCalls } = useMemo(() => {
    const inbound = callsArray.filter((c) => c.call_type === "INBOUND").length;
    const outbound = callsArray.filter((c) => c.call_type === "OUTBOUND").length;
    return { inboundCount: inbound, outboundCount: outbound, totalCalls: inbound + outbound };
  }, [callsArray]);

  const STATUS_COLORS = {
  ANSWERED: "#22c55e", // A clean Emerald/Green
};
  const getDynamicColor = (index) => `hsl(${(index * 137.5) % 360}, 70%, 50%)`;

  const callStatusData = useMemo(() => {
  if (!callsArray.length) return [];
  const map = {};
  
  callsArray.forEach((c) => {
    if (!c.call_status_disposition) return;
    const status = c.call_status_disposition.trim().toUpperCase().replace(/[\s-]+/g, " ");
    map[status] = (map[status] || 0) + 1;
  });

  return Object.entries(map).map(([name, count], index) => {
    const isAnswered = name === "ANSWERED";
    
    // If it's Answered, use the hardcoded green.
    // Otherwise, generate a dynamic color, but offset the index to 
    // ensure variety and avoid overlapping with common green HSL ranges.
    const color = isAnswered 
      ? STATUS_COLORS.ANSWERED 
      : `hsl(${(index * 137.5 + 60) % 360}, 65%, 50%)`; 

    return {
      name: name.charAt(0) + name.slice(1).toLowerCase(),
      count,
      percentage: totalCalls ? ((count / totalCalls) * 100).toFixed(1) : 0,
      color: color,
    };
  });
}, [callsArray, totalCalls]);

  const pieOptions = useMemo(() => ({
    chart: { type: "pie", height: 240, backgroundColor: "transparent" },
    title: {
      text: `<div style="text-align:center"><div style="font-size:26px;font-weight:900;color:#1e293b">${totalCalls}</div><div style="font-size:12px;color:#64748b;font-weight:700;letter-spacing:1px;text-transform:uppercase">Total Calls</div></div>`,
      align: "center", verticalAlign: "middle", useHTML: true,
    },
    plotOptions: { pie: { innerSize: "78%", dataLabels: { enabled: false }, borderWidth: 0 } },
    series: [{
      name: "Calls",
      data: [
        { name: "Inbound", y: callType === "OUTBOUND" ? 0 : inboundCount, color: "#3b82f6" },
        { name: "Outbound", y: callType === "INBOUND" ? 0 : outboundCount, color: "#8b5cf6" },
      ],
    }],
    credits: { enabled: false },
  }), [inboundCount, outboundCount, totalCalls, callType]);

  return (
    <Dialog 
      open={open} 
      fullWidth 
      maxWidth={false} 
      sx={{ "& .MuiPaper-root": { width: 620, borderRadius: "28px", overflow: "hidden", bgcolor: "#f4f7fa" } }}
    >
      {/* HEADER WITH UPDATED CONFERENCE CALL NOTE */}
      <Box sx={{ bgcolor: "#ffffff", p: 3, pb: 2.5, borderBottom: "1px solid #edf2f7" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography sx={{ fontWeight: 900, fontSize: "1.4rem", color: "#1a202c" }}>
            Call Analytics Summary
          </Typography>
          <IconButton onClick={onClose} size="small" sx={{ bgcolor: "#f7fafc", border: '1px solid #e2e8f0' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Impactful Conference Call Note */}
        <Box sx={{ 
          display: 'flex', 
          gap: 1.5, 
          bgcolor: '#f0f9ff', 
          p: 1.5, 
          borderRadius: '12px', 
          border: '1px solid #bae6fd' 
        }}>
          <InfoIcon sx={{ color: '#0284c7', fontSize: '1.2rem' }} />
<Typography variant="caption" sx={{ color: "#0369a1", fontWeight: 600, lineHeight: 1.5, fontSize: "0.8rem" }}>
            <b style={{ textTransform: "uppercase", fontSize: "11px", letterSpacing: "0.5px", color: "#026aa2", display: "block", marginBottom: "2px" }}>
              Reporting Note:
            </b>
            Dashboard totals exclude <b>Conference Calls</b>. To provide a full view of agent activity, they are <b>included here</b>, which may result in a higher total call count.
          </Typography>
        </Box>
      </Box>

      <DialogContent sx={{ p: 3, display: "flex", flexDirection: "column", gap: 3.5 }}>
        {loading ? (
          <Typography align="center" sx={{ py: 10, fontWeight: 700, color: "#a0aec0" }}>Fetching latest statistics...</Typography>
        ) : (
          <>
            {/* CARD 1: TYPE DISTRIBUTION */}
            <Box sx={{ bgcolor: "#fff", p: 3, borderRadius: "24px", boxShadow: "0 10px 15px -3px rgba(0,0,0,0.04)" }}>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 2, fontSize: "1.1rem", color: "#2d3748" }}>Call Type Distribution</Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 5 }}>
                <Box sx={{ width: 230, minWidth: 230 }}>
                  <HighchartsReact highcharts={Highcharts} options={pieOptions} />
                </Box>
                <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
                  <LegendCard label="Inbound" count={inboundCount} color="#3b82f6" total={totalCalls} active={callType === "INBOUND"} onClick={() => setSelectedCallType("INBOUND")} />
                  <LegendCard label="Outbound" count={outboundCount} color="#8b5cf6" total={totalCalls} active={callType === "OUTBOUND"} onClick={() => setSelectedCallType("OUTBOUND")} />
                </Box>
              </Box>
            </Box>

            {/* CARD 2: CALL STATUS STATISTICS */}
            <Box sx={{ bgcolor: "#fff", p: 3, borderRadius: "24px", boxShadow: "0 10px 15px -3px rgba(0,0,0,0.04)" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 800, fontSize: "1.1rem", color: "#2d3748" }}>Call Status Breakdown</Typography>
                {callType && (
                  <Typography variant="caption" onClick={() => setSelectedCallType("")} sx={{ color: "#3b82f6", cursor: "pointer", fontWeight: 800, bgcolor: '#ebf4ff', px: 1.5, py: 0.5, borderRadius: '20px' }}>
                    Reset Filter
                  </Typography>
                )}
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 3.5 }}>
                {callStatusData.map((status, i) => (
                  <Box key={i}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.2 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: status.color, boxShadow: `0 0 8px ${status.color}60` }} />
                        <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: "#4a5568" }}>{status.name}</Typography>
                      </Box>
                      <Box sx={{ display: "flex", gap: 4 }}>
                        <Typography sx={{ fontWeight: 900, color: "#1a202c", fontSize: "1rem" }}>{status.count}</Typography>
                        <Typography sx={{ color: "#a0aec0", fontWeight: 700, width: 45, textAlign: "right", fontSize: "0.85rem" }}>{status.percentage}%</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ width: "100%", height: 10, bgcolor: "#edf2f7", borderRadius: 10, overflow: "hidden" }}>
                      <Box sx={{ width: `${status.percentage}%`, height: "100%", bgcolor: status.color, borderRadius: 10, transition: 'width 1.2s cubic-bezier(0.4, 0, 0.2, 1)' }} />
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const LegendCard = ({ label, count, color, total, active, onClick }) => (
  <Box 
    onClick={onClick} 
    sx={{
      p: 2, 
      px: 3, 
      borderRadius: "16px", 
      cursor: "pointer", 
      transition: "all 0.3s ease",
      // Active State: Solid Border and tinted background
      border: `2.5px solid ${active ? color : "#f1f5f9"}`, 
      bgcolor: active ? `${color}10` : "#fff",
      display: "flex", 
      justifyContent: "space-between", 
      alignItems: "center",
      // boxShadow: active ? `0 8px 20px -6px ${color}60` : "0 2px 4px rgba(0,0,0,0.02)",
      "&:hover": {
        borderColor: color,
        transform: "translateY(-2px)"
      }
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: color }} />
        <Typography sx={{ color: active ? color : "#475569", fontWeight: 800, fontSize: "1.1rem" }}>{label}</Typography>
    </Box>
    <Box sx={{ textAlign: "right" }}>
      <Typography sx={{ fontWeight: 900, fontSize: "1.4rem", color: active ? color : "#1e293b", lineHeight: 1 }}>{count}</Typography>
      <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 800 }}>
        {total ? ((count / total) * 100).toFixed(1) : 0}%
      </Typography>
    </Box>
  </Box>
);

const mapStateToProps = (state) => ({
  loading: state.callAgentHistory.loading,
  recentHisCalls: state.callAgentHistory.recentHisCalls,
  agentId: state.agentData.agentId,
});

const mapDispatchToProps = (dispatch) => ({
  onCallAgentHistory: (agentId, date, limit, disp, type) => dispatch(actions.callAgentHistory(agentId, date, limit, disp, type)),
});

export default connect(mapStateToProps, mapDispatchToProps)(React.memo(CallStatisticsModal));